import Service, { type Registry as Services, service } from '@ember/service';
import type { OmitEmberDataTypes } from 'frontend-utils/utils/ember-data';
import type { Day as DayModel, Demand } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

export type Day = OmitEmberDataTypes<DayModel>;
export type DayForAdd = Pick<Day, 'id' | 'date'>;

export default class RepositoriesDayService extends Service {
  @service declare private store: Services['store'];

  add(day: DayForAdd): Day {
    return this.store.createRecord<DayModel>('day', day);
  }

  /**
   * Loading demand records links them onto the matching `Day` records
   * already in the store via the `demand` belongsTo relationship.
   * @param startDate - Date formatted as YYYY-MM-DD
   * @param endDate - Date formatted as YYYY-MM-DD
   */
  // We disabled the naming convention rule here because this requires a specific endpoint,
  // but returns a model that is not the same as the one used in the endpoint.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  async findDemandByDateRange({
    hotelId,
    startDate,
    endDate,
    typeKey,
  }: {
    endDate: string;
    hotelId: string;
    startDate: string;
    typeKey: string;
  }): Promise<Demand[]> {
    return this.store.query<Demand>('demand', {
      namespace: APIGATEWAY,
      typeKey,
      subscription_id: hotelId,
      from_date_range_start: startDate,
      from_date_range_end: endDate,
    });
  }

  /**
   * @param startDate - Date formatted as YYYY-MM-DD
   * @param endDate - Date formatted as YYYY-MM-DD
   */
  // We disabled the naming convention rule here because this requires a specific endpoint,
  // but returns a model that is not the same as the one used in the endpoint.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  async findEventsByDateRange({
    hotelId,
    startDate,
    endDate,
  }: {
    endDate: string;
    hotelId: string;
    startDate: string;
  }): Promise<Day[]> {
    return this.store.query<Day>('day', {
      namespace: APIGATEWAY,
      typeKey: 'events',
      hotel_id: hotelId,
      from_date_range_start: startDate,
      from_date_range_end: endDate,
    });
  }

  /**
   * Technically similar to findByEventDateRange, but this is a different endpoint,
   * maybe we should create a different repository for holidays and events?
   * Though they both use the same model so there's an inconsistency between backend / frontend
   * here.
   * @param startDate - Date formatted as YYYY-MM-DD
   * @param endDate - Date formatted as YYYY-MM-DD
   */
  // We disabled the naming convention rule here because this requires a specific endpoint,
  // but returns a model that is not the same as the one used in the endpoint.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  async findHolidaysByDateRange({
    hotelId,
    startDate,
    endDate,
  }: {
    endDate: string;
    hotelId: string;
    startDate: string;
  }): Promise<Day[]> {
    return this.store.query<Day>('day', {
      namespace: APIGATEWAY,
      typeKey: 'holidays',
      from_date_range_start: startDate,
      from_date_range_end: endDate,
      hotel_id: hotelId,
    });
  }

  /**
   * @param date - Date formatted as YYYY-MM-DD
   */
  peekByDate(date: string): Day | null {
    return this.store.peekRecord<DayModel>('day', date);
  }

  removeAll(): void {
    this.store.unloadAll('day');
  }
}

declare module '@ember/service' {
  interface Registry {
    'repositories/day': RepositoriesDayService;
  }
}
